import { useTranslation } from "next-i18next";
import { ScrollView } from "../UI/ScrollView";
import Image from "next/legacy/image";
import { formatThousand, isMobile } from "utils";
import UncheckedIcon from "../../public/icon/unchecked2.svg";
import CheckedIcon from "../../public/icon/checked2.svg";
import Button from "../UI/Button";
import classnames from "classnames";
import { ContractInfo } from "./contract-info";
import { Confirmed } from "./confirmed";
import { translate } from "utils/i18n";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { goodExchangeApi } from "utils/api";
import { BootstrapInput } from "../UI/BootstrapInput";
import { ErrorTips } from "../UI/ErrorTips";
import BackIcon from "../../public/img/mall/back.svg";
import DownloadIcon from "../../public/img/download.svg";
import { SubGoodsTypeEnum } from "@/constants/mall";
import { WlCertificate } from "../wl-certificate";
import * as htmlToImage from "html-to-image";
import FileSaver from "file-saver";
import dayjs from "dayjs";
import InfoBlackIcon from "../../public/icon/info-black.svg";
import { Tooltip } from "../UI/Tooltip";
import { useRouter } from "next/router";
import { LottiePlayer } from "../common/lottie-player";
import { ItemBox } from "./item-box";
import { debounce } from "@mui/material";

const WlInfo = ({ item, formValues, formErrors, onFieldChange }) => {
  const { t } = useTranslation("mall");

  return (
    <div>
      <div className="text-[22px] leading-[32px] font-bold font-DIN_Alternate mb-[24px]">
        {t("wlInfoTitle")}
      </div>

      <div className="mb-[24px]">
        <div className="font-bold mt-[26px] mb-[16px] flex items-center">
          <div>
            {t("wlAddress")}
            <span className="text-[#FF2800] ml-[4px]">*</span>
          </div>

          {formErrors?.wlAddress && (
            <ErrorTips className="ml-[8px]">{formErrors?.wlAddress}</ErrorTips>
          )}

          <Tooltip
            title={
              item.subGoodsType === SubGoodsTypeEnum.WHITELIST_CONTRACT
                ? t("wlDTCTips")
                : t("wlRoleTips")
            }
            arrow
            placement="top"
          >
            <span className="ml-[8px] h-[12px]">
              <InfoBlackIcon className="w-[12px] h-[12px] align-top cursor-pointer" />
            </span>
          </Tooltip>
        </div>
        <div>
          <BootstrapInput
            className="w-full"
            placeholder={t("wlAddressPlaceholder")}
            value={formValues?.wlAddress}
            onChange={(event) => {
              onFieldChange("wlAddress", event.target.value);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const Confirm = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  onConfirmRedeem,
  validateField,
  title,
  windowHeight,
}) => {
  const { t } = useTranslation("mall");
  const [step, setStep] = useState(1);

  const handleNext = () => {
    if (!validateField("email", formValues?.email)) {
      return;
    }

    if (!validateField("discordId", formValues?.discordId)) {
      return;
    }

    setStep(2);
  };

  return (
    <div className="bg-white pb-[40px]">
      <div className="h-[80px] flex items-center justify-center font-DIN_Alternate font-bold text-[24px] border-0 border-b border-solid border-black relative">
        {t("orderConfirmTitle")}

        {step > 1 && (
          <button
            className="absolute top-1/2 left-[24px] -translate-y-1/2 h-[32px] px-[16px] btn-unstyled rounded-full flex justify-center items-center align-middle text-[16px] transition border border-black border-solid hover:text-[#ff2800] hover:border-[#ff2800] font-medium"
            onClick={() => {
              setStep(step - 1);
            }}
          >
            <BackIcon />
            <span className="ml-[8px]">{t("backBtn")}</span>
          </button>
        )}
      </div>

      <div className="w-[400px] mx-auto mt-[24px]">
        <ItemBox
          img={item.mainPic}
          title={title}
          postage={0}
          cupPoints={item.cupPoints}
        />
        <div className="h-[1px] bg-[#E3E5E7] scale-y-50 my-[24px]"></div>

        <div className="relative">
          <ScrollView
            className="overflow-auto pr-[24px] w-[424px] pb-[24px]"
            style={{ maxHeight: Math.min(442, windowHeight - 400) }}
          >
            <div>
              {step === 1 && (
                <ContractInfo
                  formValues={formValues}
                  formErrors={formErrors}
                  onFieldChange={onFieldChange}
                  shouldValidateDiscordId
                />
              )}

              {step === 2 && (
                <>
                  <WlInfo
                    item={item}
                    formValues={formValues}
                    formErrors={formErrors}
                    onFieldChange={onFieldChange}
                  />

                  <div className="flex items-center">
                    <button
                      className="btn-unstyled h-[16px]"
                      onClick={() => {
                        setShouldSave(!shouldSave);
                      }}
                    >
                      {shouldSave ? (
                        <CheckedIcon className="w-[16px]" />
                      ) : (
                        <UncheckedIcon className="w-[16px]" />
                      )}
                    </button>
                    <span className="ml-[8px] text-[14px] font-medium transition">
                      {t("saveTips")}
                    </span>
                  </div>
                </>
              )}
            </div>
          </ScrollView>
          <div className="absolute bottom-[-2px] w-full h-[24px] bg-gradient-to-t from-white to-[rgba(255,255,255,0.0001)]"></div>
        </div>

        <div className="mt-[24px]">
          {step === 1 && (
            <Button
              variant="contained"
              color="secondary"
              component="span"
              className={classnames(
                "w-full h-full rounded-full transition font-black text-[18px]",
                "text-white hover:opacity-80"
              )}
              onClick={handleNext}
            >
              <span>{t("nextStepBtn")}</span>
            </Button>
          )}

          {step === 2 && (
            <Button
              variant="contained"
              color="secondary"
              component="span"
              className={classnames(
                "w-full h-full rounded-full transition font-black text-[18px]",
                "text-white hover:opacity-80"
              )}
              onClick={debounce(onConfirmRedeem, 300)}
            >
              <span>{t("btnConfirm")}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const Success = ({ item, goodExchangeData, title, windowHeight }) => {
  const { t } = useTranslation("mall");
  const domRef = useRef();

  const isPC = !isMobile();

  const saveImg = () => {
    console.log("saveImg", domRef.current);

    try {
      htmlToImage.toBlob(domRef.current).then(function (blob) {
        FileSaver.saveAs(blob, title + ".png");
      });
    } catch (error) {
      console.error("saveImg", error);
    }
  };

  return (
    <ScrollView
      className="overflow-auto relative"
      style={{ maxHeight: windowHeight - 32 }}
    >
      <LottiePlayer />

      <Confirmed
        extra={
          <div className="mt-[16px] w-[400px] mx-auto">
            <div className="border border-solid border-[#FF2800] rounded-[6px] px-[8px] py-[4px] bg-[rgba(255,40,0,0.06)] text-[#FF2800] text-[12px] font-normal leading-[16px]">
              {item.subGoodsType === SubGoodsTypeEnum.WHITELIST_CONTRACT
                ? t("wlDTCTips")
                : t("wlRoleTips")}
            </div>

            {isPC &&
              item.subGoodsType != SubGoodsTypeEnum.WHITELIST_CONTRACT && (
                <Button
                  variant="contained"
                  color="secondary"
                  component="span"
                  className={classnames(
                    "w-full h-[56px] rounded-full transition font-black text-[18px] mt-[32px]",
                    "text-white hover:opacity-80"
                  )}
                  onClick={saveImg}
                >
                  <DownloadIcon />
                  <span className="ml-[4px]">{t("wlSaveImageBtn")}</span>
                </Button>
              )}
          </div>
        }
      />

      {item.subGoodsType != SubGoodsTypeEnum.WHITELIST_CONTRACT && (
        <div className="mt-[4px]">
          <WlCertificate
            elRef={domRef}
            title={title}
            certificateNumber={goodExchangeData?.goodsNumber}
            time={dayjs(goodExchangeData?.goodsTime * 1000).format(
              "MMMM DD, YYYY"
            )}
            discordId={goodExchangeData?.attachedParams?.discordId}
          />
        </div>
      )}
    </ScrollView>
  );
};

export const ItemModalOrderWl = ({
  item,
  formValues,
  formErrors,
  onFieldChange,
  shouldSave,
  setShouldSave,
  validateField,
  title,
  windowHeight,
}) => {
  const [step, setStep] = useState(1);
  const [goodExchangeData, setGoodExchangeData] = useState();
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirmRedeem = async () => {
    if (!validateField("wlAddress", formValues?.wlAddress)) {
      return;
    }

    if (!validateField("discordId", formValues?.discordId)) {
      return;
    }

    const goodExchangeApiRes = await goodExchangeApi({
      goodsId: item.goodsId,
      contact: {
        emailAddress: formValues.email,
        twitterHandle: formValues.twitterHandle,
        phoneNumber: formValues.phone,
      },
      walletAddress: formValues.wlAddress,
      discordId: formValues.discordId,
      isRemembered: shouldSave,
    });
    console.log("goodExchangeApiRes", { goodExchangeApiRes });

    if (goodExchangeApiRes.err) {
      enqueueSnackbar(goodExchangeApiRes?.err?.response?.data?.message, {
        variant: "error",
      });
      return;
    }

    setGoodExchangeData(goodExchangeApiRes.data);

    setStep(2);
  };

  console.log("ItemModalOrderWl", { item });

  return (
    <>
      {step === 1 && (
        <Confirm
          item={item}
          formValues={formValues}
          formErrors={formErrors}
          onFieldChange={onFieldChange}
          shouldSave={shouldSave}
          setShouldSave={setShouldSave}
          onConfirmRedeem={handleConfirmRedeem}
          validateField={validateField}
          title={title}
          windowHeight={windowHeight}
        />
      )}

      {step === 2 && (
        <Success
          item={item}
          goodExchangeData={goodExchangeData}
          title={title}
          windowHeight={windowHeight}
        />
      )}
    </>
  );
};
